<template>
  <div class="mv2-editor">
    <!-- ============================================ -->
    <!--         HEADER : Locales + Subject + Actions  -->
    <!-- ============================================ -->
    <div class="mv2-header">
      <div class="mv2-header-left">
        <div class="mv2-locale-tabs">
          <button
            v-for="locale in mailType.available_locales"
            :key="locale"
            type="button"
            class="mv2-locale-tab"
            :class="{ 'mv2-locale-tab--active': currentLocale === locale }"
            @click="switchLocale(locale)"
          >
            {{ locale.toUpperCase() }}
          </button>
        </div>
        <!-- Copy locale -->
        <el-dropdown
          v-if="mailType.available_locales && mailType.available_locales.length > 1"
          trigger="click"
          @command="copyFromLocale"
        >
          <button type="button" class="mv2-icon-btn" :title="$t('MAIL_V2.COPY_FROM_LOCALE')">
            <i class="fal fa-copy"></i>
          </button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="locale in otherLocales"
              :key="locale"
              :command="locale"
            >
              {{ locale.toUpperCase() }} → {{ currentLocale.toUpperCase() }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div class="mv2-header-subject">
        <span class="mv2-subject-label">{{ $t("MAIL_V2.SUBJECT") }}</span>
        <input
          ref="subjectInput"
          type="text"
          class="mv2-input"
          :class="{ 'mv2-input--error': validationErrors['subject.' + currentLocale] }"
          v-model="form.subject[currentLocale]"
          :placeholder="$t('MAIL_V2.SUBJECT') + ' (' + currentLocale.toUpperCase() + ')'"
          @focus="lastFocusedInput = 'subject'"
          @input="clearValidationError('subject.' + currentLocale)"
        />
      </div>
      <div class="mv2-header-actions">
        <button v-if="isDefaultEdit" type="button" class="mv2-icon-btn" :class="{ 'mv2-icon-btn--active': typeSettingsModal }" @click="openTypeSettings" :title="$t('MAIL_V2.TYPE_SETTINGS')">
          <i class="fal fa-cog"></i>
        </button>
        <button v-if="!isDefaultEdit" type="button" class="mv2-icon-btn" :class="{ 'mv2-icon-btn--active': showSender }" @click="showSender = !showSender" :title="$t('MAIL_V2.SENDER_OVERRIDE')">
          <i class="fal fa-envelope-open"></i>
        </button>
        <button type="button" class="mv2-icon-btn" @click="sendTestModal = true" :title="$t('MAIL_V2.SEND_TEST')">
          <i class="fal fa-paper-plane"></i>
        </button>
        <div class="mv2-header-sep"></div>
        <button type="button" class="mv2-btn mv2-btn--secondary" @click="$emit('cancel')">
          {{ $t("COMMON.CANCEL") }}
        </button>
        <button type="button" class="mv2-btn mv2-btn--primary" :disabled="saving" @click="save">
          <i class="fas fa-spinner fa-spin" v-if="saving"></i>
          <i class="fal fa-save" v-else></i>
          {{ $t("COMMON.SAVE") }}
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!--          SENDER OVERRIDE (slide down)         -->
    <!-- ============================================ -->
    <transition name="slide">
      <div class="mv2-sender" v-if="showSender">
        <div class="mv2-sender-grid">
          <div class="mv2-sender-field">
            <label>{{ $t("MAIL_V2.FROM_NAME") }}</label>
            <input type="text" class="mv2-input" v-model="form.metadata.from_name" />
          </div>
          <div class="mv2-sender-field">
            <label>{{ $t("MAIL_V2.FROM_EMAIL") }}</label>
            <input type="email" class="mv2-input" v-model="form.metadata.from_email" />
          </div>
          <div class="mv2-sender-field">
            <label>{{ $t("MAIL_V2.REPLY_TO") }}</label>
            <input type="email" class="mv2-input" v-model="form.metadata.reply_to_email" />
          </div>
        </div>
      </div>
    </transition>

    <!-- ============================================ -->
    <!--         VARIABLES BAR (compact)                -->
    <!-- ============================================ -->
    <div class="mv2-variables" v-if="mailType.variables && mailType.variables.length">
      <span class="mv2-variables-label">
        <i class="fal fa-code"></i>
      </span>
      <div class="mv2-variables-list">
        <button
          type="button"
          v-for="v in mailType.variables"
          :key="v.key"
          class="mv2-var-chip"
          @mousedown.prevent="insertVariable(v.key)"
          :title="getLocale(v.label)"
        >
          <span v-text="v.key"></span>
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!--          GRAPESJS EDITOR (full height)        -->
    <!-- ============================================ -->
    <div class="mv2-builder">
      <div class="mv2-gjs" ref="gjsEditor"></div>
    </div>

    <!-- ============================================ -->
    <!--            SEND TEST DIALOG                   -->
    <!-- ============================================ -->
    <el-dialog :visible.sync="sendTestModal" :title="$t('MAIL_V2.SEND_TEST')" width="420px" append-to-body>
      <div class="mv2-test-form">
        <div class="mv2-test-field">
          <label>{{ $t("MAIL_V2.TEST_EMAIL") }}</label>
          <input type="email" class="mv2-input" v-model="testEmail" placeholder="test@example.com" />
        </div>
        <div class="mv2-test-field">
          <label>{{ $t("MAIL_V2.LANGUAGE") }}</label>
          <el-select v-model="testLocale" class="w-100" size="small">
            <el-option v-for="locale in mailType.available_locales" :key="locale" :value="locale" :label="locale.toUpperCase()" />
          </el-select>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <button class="mv2-btn mv2-btn--secondary" @click="sendTestModal = false">{{ $t("COMMON.CANCEL") }}</button>
        <button class="mv2-btn mv2-btn--primary" @click="sendTest" :disabled="!testEmail">
          <i class="fal fa-paper-plane"></i> {{ $t("MAIL_V2.SEND") }}
        </button>
      </span>
    </el-dialog>

    <!-- ============================================ -->
    <!--          TYPE SETTINGS DIALOG                 -->
    <!-- ============================================ -->
    <el-dialog
      :visible.sync="typeSettingsModal"
      :title="$t('MAIL_V2.TYPE_SETTINGS')"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
    >
      <form class="add-form pb-3" @submit.prevent="saveTypeSettings" @keydown.enter.prevent="() => {}">
        <!-- Name per locale -->
        <div
          v-for="locale in typeSettingsForm.available_locales"
          :key="'ts-name-' + locale"
          class="form-wrapper full"
        >
          <div class="row">
            <div class="col-6">
              <base-input
                :label="$t('COMMON.NAME') + ' (' + locale.toUpperCase() + ') (*)'"
                v-model="typeSettingsForm.name[locale]"
              />
            </div>
            <div class="col-6">
              <base-input :label="$t('MAIL_V2.DESCRIPTION') + ' (' + locale.toUpperCase() + ')'">
                <textarea class="form-control" v-model="typeSettingsForm.description[locale]" rows="2"></textarea>
              </base-input>
            </div>
          </div>
        </div>

        <!-- Locales -->
        <div class="form-wrapper">
          <base-input :label="$t('MAIL_V2.LANGUAGES') + ' (*)'">
            <el-select v-model="typeSettingsForm.available_locales" multiple filterable @change="onLocalesChanged">
              <el-option value="fr" label="Français (FR)" />
              <el-option value="en" label="English (EN)" />
              <el-option value="es" label="Español (ES)" />
              <el-option value="pt" label="Português (PT)" />
              <el-option value="ar" label="العربية (AR)" />
            </el-select>
          </base-input>
        </div>

        <!-- Category -->
        <div class="form-wrapper">
          <base-input :label="$t('MAIL_V2.CATEGORY')">
            <el-select v-model="typeSettingsForm.category" filterable>
              <el-option value="transactional" :label="$t('MAIL_V2.TRANSACTIONAL')" />
              <el-option value="notification" :label="$t('MAIL_V2.NOTIFICATION')" />
              <el-option value="marketing" :label="$t('MAIL_V2.MARKETING')" />
            </el-select>
          </base-input>
        </div>

        <!-- Variables -->
        <div class="form-wrapper full">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h4 class="mb-0">{{ $t("MAIL_V2.VARIABLES") }}</h4>
            <base-button class="elite-button add" icon size="sm" @click="addTypeVariable">
              <span class="btn-inner--icon"><i class="far fa-plus-circle"></i></span>
              <span class="btn-inner--text">{{ $t("MAIL_V2.ADD_VARIABLE") }}</span>
            </base-button>
          </div>

          <div v-if="!typeSettingsForm.variables.length" class="text-muted mb-2">
            {{ $t("MAIL_V2.NO_VARIABLES_YET") }}
          </div>

          <div
            v-for="(variable, idx) in typeSettingsForm.variables"
            :key="'tsvar-' + idx"
            class="d-flex align-items-end gap-2 mb-2"
            style="gap: 8px;"
          >
            <div style="flex: 2;">
              <base-input label="Key (*)" v-model="variable.key" placeholder="customer_name" />
            </div>
            <div style="flex: 2;">
              <base-input
                :label="'Label (' + (typeSettingsForm.available_locales[0] || 'fr').toUpperCase() + ')'"
                v-model="variable.label[typeSettingsForm.available_locales[0] || 'fr']"
                placeholder="Nom du client"
              />
            </div>
            <div style="flex: 1;">
              <base-input :label="$t('MAIL_V2.GROUP')" v-model="variable.group" placeholder="customer" />
            </div>
            <div style="flex: 1;">
              <base-input label="Sample" v-model="variable.sample_value" placeholder="Jean Dupont" />
            </div>
            <div style="flex-shrink: 0; padding-bottom: 14px;">
              <a @click="typeSettingsForm.variables.splice(idx, 1)" class="text-danger" style="cursor:pointer;">
                <i class="fal fa-trash-alt"></i>
              </a>
            </div>
          </div>
        </div>
      </form>

      <span slot="footer" class="dialog-footer">
        <button class="mv2-btn mv2-btn--secondary" @click="typeSettingsModal = false">{{ $t("COMMON.CANCEL") }}</button>
        <button class="mv2-btn mv2-btn--primary" @click="saveTypeSettings" :disabled="savingTypeSettings">
          <i class="fas fa-spinner fa-spin" v-if="savingTypeSettings"></i>
          <i class="fal fa-save" v-else></i>
          {{ $t("COMMON.SAVE") }}
        </button>
      </span>
    </el-dialog>

    <!-- Autocomplete dropdown for {{ variables -->
    <div
      v-if="autocomplete.visible"
      class="mv2-autocomplete"
      :style="{ left: autocomplete.x + 'px', top: autocomplete.y + 'px' }"
    >
      <div class="mv2-autocomplete-header">Variables</div>
      <div
        v-for="(item, idx) in filteredAutocompleteItems"
        :key="item.key"
        class="mv2-autocomplete-item"
        :class="{ 'mv2-autocomplete-item--active': idx === autocomplete.selectedIndex }"
        @mousedown.prevent="selectAutocomplete(item)"
        @mouseenter="autocomplete.selectedIndex = idx"
      >
        <span class="mv2-autocomplete-key" v-text="'{{ ' + item.key + ' }}'"></span>
        <span class="mv2-autocomplete-label">{{ getLocale(item.label) }}</span>
      </div>
      <div v-if="!filteredAutocompleteItems.length" class="mv2-autocomplete-empty">
        {{ $t("MAIL_V2.NO_MATCH") }}
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { Select, Option, Dialog, Dropdown, DropdownMenu, DropdownItem } from "element-ui";
import grapesjs from "grapesjs/dist/grapes.min.js";
import grapesjsPresetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import { registerWidgets } from "../config/mailWidgets";

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },

  props: {
    templateData: { type: Object, required: true },
    mailType: { type: Object, required: true },
    organizationId: { type: [String, Number], required: true },
    organization: { type: Object, default: null },
    isDefaultEdit: { type: Boolean, default: false },
  },

  data() {
    const locales = this.mailType.available_locales || ["fr"];
    const firstLocale = locales[0];

    const subject = {};
    locales.forEach((l) => {
      subject[l] = this.templateData.subject?.[l] || this.mailType.default_template?.[l]?.subject || "";
    });

    return {
      currentLocale: firstLocale,
      gjsEditor: null,
      gjsProjectData: {},
      saving: false,
      autocomplete: {
        visible: false,
        x: 0,
        y: 0,
        filter: "",
        items: [],
        selectedIndex: 0,
        rteEl: null,
      },
      showSender: !this.isDefaultEdit,
      lastFocusedInput: "editor",
      sendTestModal: false,
      testEmail: "",
      testLocale: firstLocale,
      typeSettingsModal: false,
      savingTypeSettings: false,
      typeSettingsForm: {
        name: {},
        description: {},
        category: "",
        available_locales: [],
        variables: [],
      },
      form: {
        subject,
        body_html: { ...(this.templateData.body_html || {}) },
        body_json: { ...(this.templateData.body_json || {}) },
        metadata: {
          from_name: this.templateData.metadata?.from_name || this.organization?.name || "",
          from_email: this.templateData.metadata?.from_email || this.organization?.email || "",
          reply_to_email: this.templateData.metadata?.reply_to_email || this.organization?.email || "",
          reply_to_name: this.templateData.metadata?.reply_to_name || this.organization?.name || "",
        },
      },
      validationErrors: {},
    };
  },

  mounted() {
    this.$nextTick(() => this.initGrapesJs());
  },

  beforeDestroy() {
    if (this.gjsEditor) this.gjsEditor.destroy();
  },

  methods: {
    initGrapesJs() {
      const initialHtml = this.form.body_html[this.currentLocale] || "";

      this.gjsEditor = grapesjs.init({
        container: this.$refs.gjsEditor,
        fromElement: false,
        height: "100%",
        width: "auto",
        storageManager: false,
        plugins: [grapesjsPresetNewsletter],
        pluginsOpts: { [grapesjsPresetNewsletter]: {} },
        deviceManager: {
          devices: [
            { name: "Desktop", width: "" },
            { name: "Tablet", width: "600px", widthMedia: "600px" },
            { name: "Mobile portrait", width: "320px", widthMedia: "320px" },
          ],
        },
        canvas: {
          styles: [
            "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap",
          ],
        },
        protectedCss: `
          body { padding: 32px 40px !important; font-family: 'Poppins', Arial, sans-serif; color: #333; }
          * { box-sizing: border-box; }
          table { max-width: 100% !important; }
          img { max-width: 100% !important; height: auto !important; }
          @media only screen and (max-width: 480px) {
            body { padding: 16px !important; }
            table[width="100%"] td { display: block !important; width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }
            td[width="40%"], td[width="50%"], td[width="60%"] { display: block !important; width: 100% !important; }
            h1 { font-size: 22px !important; }
            h2 { font-size: 18px !important; }
          }
        `,
      });

      // Load initial content: prefer projectData (body_json) over raw HTML
      const initialJson = this.form.body_json?.[this.currentLocale];
      if (initialJson && typeof initialJson === "object" && Object.keys(initialJson).length) {
        // Load full project data (preserves styles + structure)
        this.gjsEditor.loadProjectData(initialJson);
        this.gjsProjectData[this.currentLocale] = initialJson;
      } else if (initialHtml) {
        // Fallback: parse raw HTML — extract <style> tags first
        this.loadHtmlWithStyles(initialHtml);
        this.$nextTick(() => {
          this.gjsProjectData[this.currentLocale] = this.gjsEditor.getProjectData();
        });
      }

      // Pre-load projectData for other locales too
      (this.mailType.available_locales || []).forEach((locale) => {
        if (locale === this.currentLocale) return;
        const localeJson = this.form.body_json?.[locale];
        if (localeJson && typeof localeJson === "object" && Object.keys(localeJson).length) {
          this.gjsProjectData[locale] = localeJson;
        }
      });

      this.registerVariableBlocks();
      this.registerWidgetBlocks();

      this.gjsEditor.on("change:changesCount", debounce(() => {
        this.syncEditorToForm();
      }, 600));

      // Track focus back to editor
      this.gjsEditor.on("component:selected", () => {
        this.lastFocusedInput = "editor";
      });

      // Hook into RTE for autocomplete
      this.gjsEditor.on("rte:enable", () => {
        const rteEl = this.gjsEditor.RichTextEditor?.getToolbarEl?.()?.parentElement;
        const iframeEl = this.gjsEditor.Canvas.getFrameEl();
        const iframeDoc = iframeEl?.contentDocument || iframeEl?.contentWindow?.document;

        if (iframeDoc) {
          iframeDoc.addEventListener("keyup", this.handleRteKeyup);
          iframeDoc.addEventListener("keydown", this.handleRteKeydown);
          this.autocomplete.rteEl = iframeDoc;
        }
      });

      this.gjsEditor.on("rte:disable", () => {
        this.hideAutocomplete();
        if (this.autocomplete.rteEl) {
          this.autocomplete.rteEl.removeEventListener("keyup", this.handleRteKeyup);
          this.autocomplete.rteEl.removeEventListener("keydown", this.handleRteKeydown);
          this.autocomplete.rteEl = null;
        }
      });
    },

    registerVariableBlocks() {
      if (!this.gjsEditor || !this.mailType.variables) return;
      const bm = this.gjsEditor.BlockManager;

      this.mailType.variables.forEach((v) => {
        bm.add(`var-${v.key}`, {
          label: `{{ ${v.key} }}`,
          category: "Variables",
          content: {
            type: "text",
            content: `{{ ${v.key} }}`,
            style: {
              display: "inline-block",
              padding: "2px 6px",
              background: "#e3f2fd",
              color: "#1565c0",
              "border-radius": "3px",
              "font-family": "monospace",
              "font-size": "13px",
            },
          },
        });
      });
    },

    registerWidgetBlocks() {
      if (!this.gjsEditor) return;
      registerWidgets(this.gjsEditor.BlockManager);
    },

    syncEditorToForm() {
      if (!this.gjsEditor) return;
      const html = this.gjsEditor.getHtml();
      const css = this.gjsEditor.getCss();
      this.$set(this.form.body_html, this.currentLocale, css ? `<style>${css}</style>${html}` : html);
      this.gjsProjectData[this.currentLocale] = this.gjsEditor.getProjectData();
    },

    /**
     * Load raw HTML into GrapesJS, extracting <style> tags to load as CSS separately.
     * This preserves styles when body_json is not available.
     */
    loadHtmlWithStyles(rawHtml) {
      if (!this.gjsEditor || !rawHtml) {
        if (this.gjsEditor) {
          this.gjsEditor.setComponents("");
          this.gjsEditor.setStyle("");
        }
        return;
      }

      const styleMatch = rawHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
      let css = "";
      let html = rawHtml;

      if (styleMatch) {
        css = styleMatch
          .map((tag) => tag.replace(/<\/?style[^>]*>/gi, ""))
          .join("\n");
        html = rawHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
      }

      this.gjsEditor.setComponents(html);
      // Only set CSS if we extracted some — otherwise let GrapesJS keep
      // the styles it parsed from inline style="" attributes
      if (css) {
        this.gjsEditor.setStyle(css);
      }
    },

    switchLocale(locale) {
      if (locale === this.currentLocale) return;
      this.syncEditorToForm();
      this.currentLocale = locale;

      if (this.gjsProjectData[locale]) {
        this.gjsEditor.loadProjectData(this.gjsProjectData[locale]);
      } else {
        this.loadHtmlWithStyles(this.form.body_html[locale] || "");
        this.$nextTick(() => {
          this.gjsProjectData[locale] = this.gjsEditor.getProjectData();
        });
      }
    },

    copyFromLocale(sourceLocale) {
      // Make sure source data is up-to-date
      if (sourceLocale === this.currentLocale) return;

      // Deep clone the project data from source
      const sourceProjectData = this.gjsProjectData[sourceLocale];
      const sourceHtml = this.form.body_html[sourceLocale] || "";
      const sourceSubject = this.form.subject[sourceLocale] || "";

      if (sourceProjectData) {
        // Best path: copy the full project data (preserves styles + structure)
        const clonedData = JSON.parse(JSON.stringify(sourceProjectData));
        this.gjsProjectData[this.currentLocale] = clonedData;
        this.gjsEditor.loadProjectData(clonedData);
      } else if (sourceHtml) {
        // Fallback: parse raw HTML with styles
        this.loadHtmlWithStyles(sourceHtml);
        this.$nextTick(() => {
          this.gjsProjectData[this.currentLocale] = this.gjsEditor.getProjectData();
        });
      }

      // Copy subject too
      this.$set(this.form.subject, this.currentLocale, sourceSubject);

      // Sync HTML
      this.$nextTick(() => {
        this.syncEditorToForm();
      });

      this.$notify({
        type: "success",
        message: this.$t("MAIL_V2.LOCALE_COPIED", {
          from: sourceLocale.toUpperCase(),
          to: this.currentLocale.toUpperCase(),
        }),
      });
    },

    insertVariable(key) {
      const tag = `{{ ${key} }}`;

      // If subject input was last focused, insert into subject
      if (this.lastFocusedInput === "subject") {
        const input = this.$refs.subjectInput;
        if (input) {
          const start = input.selectionStart || 0;
          const end = input.selectionEnd || start;
          const current = this.form.subject[this.currentLocale] || "";
          this.$set(this.form.subject, this.currentLocale, current.substring(0, start) + tag + current.substring(end));
          this.$nextTick(() => {
            input.focus();
            const newPos = start + tag.length;
            input.setSelectionRange(newPos, newPos);
          });
          return;
        }
      }

      if (!this.gjsEditor) return;

      // If RTE is active (inline editing), insert at cursor position
      if (this.autocomplete.rteEl) {
        const sel = this.autocomplete.rteEl.getSelection();
        if (sel && sel.rangeCount) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(this.autocomplete.rteEl.createTextNode(tag));
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
      }

      // Otherwise, append to selected text component or add new
      const selected = this.gjsEditor.getSelected();
      if (selected && selected.is("text")) {
        selected.set("content", (selected.get("content") || "") + tag);
      } else {
        this.gjsEditor.addComponents({
          type: "text",
          content: tag,
          style: { display: "inline-block", padding: "2px 6px", background: "#e3f2fd", color: "#1565c0", "border-radius": "3px", "font-family": "monospace" },
        });
      }
    },

    validateForm() {
      this.validationErrors = {};
      let isValid = true;

      // Validate subject fields for all locales
      (this.mailType.available_locales || []).forEach((locale) => {
        if (!this.form.subject[locale] || !this.form.subject[locale].trim()) {
          this.validationErrors[`subject.${locale}`] = this.$t("MAIL_V2.SUBJECT_REQUIRED") || `Le sujet en ${locale.toUpperCase()} est obligatoire`;
          isValid = false;
        }
      });

      return isValid;
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    async save() {
      // Validate form before saving
      if (!this.validateForm()) {
        this.$notify({
          type: "warning",
          message: this.$t("MAIL_V2.VALIDATION_ERROR") || "Veuillez remplir tous les champs obligatoires"
        });
        return;
      }

      this.syncEditorToForm();
      this.saving = true;

      const bodyJson = {};
      (this.mailType.available_locales || []).forEach((l) => {
        bodyJson[l] = this.gjsProjectData[l] || null;
      });

      try {
        if (this.isDefaultEdit) {
          // Save as default template on the mail type (reseller level)
          const defaultTemplates = {};
          (this.mailType.available_locales || []).forEach((l) => {
            defaultTemplates[l] = {
              subject: this.form.subject[l] || "",
              body_html: this.form.body_html[l] || "",
              body_json: bodyJson[l] || null,
            };
          });

          await this.$store.dispatch("mailV2/updateDefaultTemplate", {
            typeId: this.templateData.id,
            data: { default_templates: defaultTemplates },
          });
        } else {
          // Save as org override
          await this.$store.dispatch("mailV2/updateTemplate", {
            orgId: this.organizationId,
            templateId: this.templateData.id,
            data: {
              subject: this.form.subject,
              body_html: this.form.body_html,
              body_json: bodyJson,
              metadata: this.form.metadata,
            },
          });
        }
        this.$emit("saved");
      } catch (error) {
        // Handle backend validation errors
        if (error?.response?.data?.errors) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach((key) => {
            // Transform backend error keys (e.g., "default_templates.fr.subject") to match frontend format
            const match = key.match(/default_templates\.([a-z]+)\.subject/);
            if (match) {
              const locale = match[1];
              this.validationErrors[`subject.${locale}`] = errors[key][0];
            } else if (key.match(/subject\.([a-z]+)/)) {
              const locale = key.split('.')[1];
              this.validationErrors[`subject.${locale}`] = errors[key][0];
            }
          });
          this.$notify({
            type: "danger",
            message: error.response.data.message || this.$t("ERRORS.SOMETHING_WENT_WRONG")
          });
        } else {
          this.$notify({ type: "danger", message: this.$t("ERRORS.SOMETHING_WENT_WRONG") });
        }
      } finally {
        this.saving = false;
      }
    },

    async sendTest() {
      try {
        // Sync current editor content before sending
        this.syncEditorToForm();

        const bodyJson = {};
        (this.mailType.available_locales || []).forEach((l) => {
          bodyJson[l] = this.gjsProjectData[l] || null;
        });

        if (this.isDefaultEdit) {
          // Admin send test: compile from current editor content
          await this.$store.dispatch("mailV2/sendTestAdmin", {
            typeId: this.templateData.id,
            data: {
              locale: this.testLocale,
              email: this.testEmail,
              subject: this.form.subject,
              body_html: this.form.body_html,
            },
          });
        } else {
          await this.$store.dispatch("mailV2/sendTest", {
            orgId: this.organizationId,
            data: { type_code: this.mailType.code, locale: this.testLocale, email: this.testEmail },
          });
        }
        this.sendTestModal = false;
        this.$notify({ type: "success", message: this.$t("MAIL_V2.TEST_SENT") });
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t("ERRORS.SOMETHING_WENT_WRONG");
        this.$notify({ type: "danger", message: msg });
      }
    },

    // ---- Type Settings ----

    openTypeSettings() {
      const mt = this.mailType;
      this.typeSettingsForm = {
        name: { ...(mt.name || mt.type_name || {}) },
        description: { ...(mt.description || {}) },
        category: mt.category || "transactional",
        available_locales: [...(mt.available_locales || [])],
        variables: (mt.variables || []).map((v) => ({
          id: v.id || null,
          key: v.key,
          label: { ...(v.label || {}) },
          group: v.group || "",
          type: v.type || "string",
          is_required: v.is_required || false,
          sample_value: v.sample_value || "",
        })),
      };
      this.typeSettingsModal = true;
    },

    onLocalesChanged(newLocales) {
      // Ensure name/description objects have keys for new locales
      newLocales.forEach((l) => {
        if (!this.typeSettingsForm.name[l]) this.$set(this.typeSettingsForm.name, l, "");
        if (!this.typeSettingsForm.description[l]) this.$set(this.typeSettingsForm.description, l, "");
      });
    },

    addTypeVariable() {
      const label = {};
      this.typeSettingsForm.available_locales.forEach((l) => { label[l] = ""; });
      this.typeSettingsForm.variables.push({
        id: null,
        key: "",
        label,
        group: "",
        type: "string",
        is_required: false,
        sample_value: "",
      });
    },

    async saveTypeSettings() {
      this.savingTypeSettings = true;
      try {
        const result = await this.$store.dispatch("mailV2/updateMailType", {
          typeId: this.templateData.id,
          data: this.typeSettingsForm,
        });

        // Update the local mailType prop data
        this.$emit("typeUpdated", result);

        // Refresh variable blocks in GrapesJS
        if (result?.variables) {
          this.mailType.variables = result.variables;
          this.registerVariableBlocks();
        }
        if (result?.available_locales) {
          this.mailType.available_locales = result.available_locales;
        }

        this.typeSettingsModal = false;
        this.$notify({ type: "success", message: this.$t("MAIL_V2.TYPE_SETTINGS_SAVED") });
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t("ERRORS.SOMETHING_WENT_WRONG");
        this.$notify({ type: "danger", message: msg });
      } finally {
        this.savingTypeSettings = false;
      }
    },

    // ---- Autocomplete ----

    handleRteKeyup(e) {
      // Ignore navigation keys when autocomplete is open
      if (this.autocomplete.visible && ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) {
        return;
      }

      const sel = this.autocomplete.rteEl?.getSelection?.();
      if (!sel || !sel.rangeCount) return;

      const range = sel.getRangeAt(0);
      const textNode = range.startContainer;
      if (textNode.nodeType !== 3) { this.hideAutocomplete(); return; }

      const text = textNode.textContent.substring(0, range.startOffset);
      const match = text.match(/\{\{\s*([a-zA-Z0-9_]*)$/);

      if (match) {
        const oldFilter = this.autocomplete.filter;
        const newFilter = match[1].toLowerCase();
        this.autocomplete.filter = newFilter;
        this.autocomplete.items = (this.mailType.variables || []);

        // Only reset index if filter actually changed
        if (oldFilter !== newFilter) {
          this.autocomplete.selectedIndex = 0;
        }

        this.autocomplete.visible = true;
        this.positionAutocomplete(range);
      } else {
        this.hideAutocomplete();
      }
    },

    handleRteKeydown(e) {
      if (!this.autocomplete.visible) return;

      const items = this.filteredAutocompleteItems;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.autocomplete.selectedIndex = Math.min(this.autocomplete.selectedIndex + 1, items.length - 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.autocomplete.selectedIndex = Math.max(this.autocomplete.selectedIndex - 1, 0);
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (items.length) {
          e.preventDefault();
          this.selectAutocomplete(items[this.autocomplete.selectedIndex]);
        }
      } else if (e.key === "Escape") {
        this.hideAutocomplete();
      }
    },

    positionAutocomplete(range) {
      try {
        const iframeEl = this.gjsEditor.Canvas.getFrameEl();
        const iframeRect = iframeEl.getBoundingClientRect();
        const rangeRect = range.getBoundingClientRect();

        this.autocomplete.x = iframeRect.left + rangeRect.left;
        this.autocomplete.y = iframeRect.top + rangeRect.bottom + 4;
      } catch {
        this.autocomplete.x = 200;
        this.autocomplete.y = 300;
      }
    },

    selectAutocomplete(item) {
      const doc = this.autocomplete.rteEl;
      if (!doc) return;

      const sel = doc.getSelection();
      if (!sel || !sel.rangeCount) return;

      const range = sel.getRangeAt(0);
      const textNode = range.startContainer;
      if (textNode.nodeType !== 3) return;

      const text = textNode.textContent;
      const offset = range.startOffset;

      // Find the {{ trigger position
      const before = text.substring(0, offset);
      const triggerIdx = before.lastIndexOf("{{");
      if (triggerIdx === -1) return;

      // Replace from {{ to cursor with the full variable
      const replacement = `{{ ${item.key} }}`;
      const newText = text.substring(0, triggerIdx) + replacement + text.substring(offset);
      textNode.textContent = newText;

      // Move cursor after the inserted variable
      const newOffset = triggerIdx + replacement.length;
      range.setStart(textNode, newOffset);
      range.setEnd(textNode, newOffset);
      sel.removeAllRanges();
      sel.addRange(range);

      this.hideAutocomplete();
    },

    hideAutocomplete() {
      this.autocomplete.visible = false;
      this.autocomplete.filter = "";
      this.autocomplete.selectedIndex = 0;
    },

    getLocale(val) {
      if (!val) return "";
      if (typeof val === "string") return val;
      return val[this.$i18n?.locale || "fr"] || val.fr || val.en || "";
    },
  },

  computed: {
    otherLocales() {
      return (this.mailType.available_locales || []).filter((l) => l !== this.currentLocale);
    },
    filteredAutocompleteItems() {
      if (!this.autocomplete.filter) return this.autocomplete.items;
      const f = this.autocomplete.filter;
      return this.autocomplete.items.filter(
        (v) => v.key.toLowerCase().includes(f) || this.getLocale(v.label)?.toLowerCase().includes(f)
      );
    },
  },
};
</script>

<style>
/* ============================================
   MV2 EDITOR — COMPLETE REDESIGN
   ============================================ */
/* Override parent resizable-wrapper-content-body flex behavior */
.mv2-editor-body {
  display: block !important;
  width: 100% !important;
}

.resizable-wrapper-content-body:has(.mv2-editor) {
  display: block !important;
  width: 100% !important;
}

.mv2-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #f7f8fa;
}

/* ---- HEADER (1 ligne compacte) ---- */
.mv2-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}
.mv2-header-left {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.mv2-header-subject {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.mv2-subject-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  flex-shrink: 0;
}
.mv2-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.mv2-header-sep {
  width: 1px;
  height: 24px;
  background: #e0e0e0;
  margin: 0 4px;
}

/* ---- LOCALE TABS ---- */
.mv2-locale-tabs {
  display: flex;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 2px;
}
.mv2-locale-tab {
  padding: 4px 12px;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.5px;
}
.mv2-locale-tab:hover { color: #374151; }
.mv2-locale-tab--active {
  background: #0091ca;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 145, 202, 0.3);
}

/* ---- ICON BUTTONS ---- */
.mv2-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #9ca3af;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
}
.mv2-icon-btn:hover { color: #374151; background: #f3f4f6; }
.mv2-icon-btn--active { color: #0091ca; background: #e0f3ff; }

.mv2-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: #1f2937;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
}

.mv2-input:focus {
  border-color: #0091ca;
  box-shadow: 0 0 0 3px rgba(87, 102, 84, 0.1);
}

.mv2-input--error {
  border-color: #dc2626 !important;
}

.mv2-input--error:focus {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1) !important;
}

.mv2-error-message {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #dc2626;
  font-weight: 500;
}

/* (moved to .mv2-header-actions) */
.mv2-topbar-actions-REMOVED {
  display: none;
}

/* ---- BUTTONS ---- */
.mv2-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.mv2-btn--primary {
  background: #0091ca;
  color: #fff;
}

.mv2-btn--primary:hover { background: #007aab; }
.mv2-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }

.mv2-btn--secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.mv2-btn--secondary:hover { background: #e5e7eb; }

.mv2-btn--ghost {
  background: transparent;
  color: #6b7280;
  padding: 8px 10px;
  border-radius: 6px;
}

.mv2-btn--ghost:hover { background: #f3f4f6; color: #0091ca; }

/* ---- SENDER OVERRIDE ---- */
.mv2-sender {
  padding: 12px 20px;
  background: #fefce8;
  border-bottom: 1px solid #fde68a;
}

.mv2-sender-grid {
  display: flex;
  gap: 12px;
}

.mv2-sender-field {
  flex: 1;
}

.mv2-sender-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.2s ease;
  max-height: 100px;
  overflow: hidden;
}

.slide-enter, .slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

/* ---- VARIABLES BAR ---- */
.mv2-variables {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: #fafbfc;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  scrollbar-width: thin;
}

.mv2-variables::-webkit-scrollbar { height: 3px; }
.mv2-variables::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

.mv2-variables-label {
  font-size: 13px;
  color: #9ca3af;
  flex-shrink: 0;
}

.mv2-variables-list {
  display: flex;
  gap: 4px;
  overflow-x: auto;
}

.mv2-var-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #fff;
  color: #0091ca;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: 'Fira Code', 'SF Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.1s;
  white-space: nowrap;
}

.mv2-var-chip:hover {
  background: #0091ca;
  color: #fff;
  border-color: #0091ca;
}

/* ---- BUILDER ---- */
.mv2-builder {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}

/* (locale tabs moved to header) */

/* ---- GRAPESJS ---- */
.mv2-gjs {
  flex: 1;
  min-height: 600px;
  width: 100%;
  overflow: hidden;
}

.mv2-gjs > div {
  height: 100% !important;
  width: 100% !important;
  border: none !important;
}

/* GrapesJS theme overrides */
.gjs-one-bg { background-color: #f9fafb !important; }
.gjs-two-color { color: #374151 !important; }
.gjs-three-bg { background-color: #0091ca !important; }
.gjs-four-color, .gjs-four-color-h:hover { color: #0091ca !important; }

.gjs-pn-panel { padding: 0 !important; }

.gjs-cv-canvas {
  background-color: #e5e7eb !important;
}

/* Force GrapesJS editor to use full width */
.gjs-editor {
  width: 100% !important;
}

.gjs-editor-cont {
  width: 100% !important;
}


/* ---- TEST DIALOG ---- */
.mv2-test-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mv2-test-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.dialog-footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* ---- AUTOCOMPLETE DROPDOWN ---- */
.mv2-autocomplete {
  position: fixed;
  z-index: 99999;
  min-width: 260px;
  max-width: 380px;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-family: inherit;
}

.mv2-autocomplete-header {
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #9ca3af;
  border-bottom: 1px solid #f3f4f6;
  letter-spacing: 0.5px;
}

.mv2-autocomplete-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
}

.mv2-autocomplete-item:hover,
.mv2-autocomplete-item--active {
  background: #eff6ff;
}

.mv2-autocomplete-key {
  font-family: 'Fira Code', 'SF Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #1d4ed8;
  white-space: nowrap;
}

.mv2-autocomplete-label {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mv2-autocomplete-empty {
  padding: 10px 12px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
}
</style>
